import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { useHistory, useLocation, useParams } from 'react-router'
import { GOAL_GROUP_GRANTEES_INFO } from 'Apollo/Queries/GoalGroupGranteesInfo'
import { useAppState } from 'Context/AppContext/AppContext'
import { GoalGroupGranteeInfo } from 'Generated/graphql-types'
import { GranteesSection } from './GranteesSection'
import {
  Details,
  GoalSectionsContainer,
  MenuTab,
  MenuTabs,
} from './FunderGoalSections.styled'
import { TaskCompletionSection } from './TaskCompletionSection'
import { ProgressChartSection } from './ProgressChartSection'
import { SectionParamOptionsEnum, SectionTab } from '../types'
import { sendFunderGoalTabClickedEvent } from '../utils'

export const SECTION_PARAM_KEY = 'section'

type FunderGoalSectionProps = {
  goalGroupTargetDescription: string
}
export const FunderGoalSections = ({
  goalGroupTargetDescription,
}: FunderGoalSectionProps) => {
  const {
    funderOrgConfig: { orgId: funderOrgId },
    userConfig: { id, email },
  } = useAppState()
  const history = useHistory()
  const location = useLocation()

  const sectionParam = new URLSearchParams(location.search).get(
    SECTION_PARAM_KEY
  ) as SectionParamOptionsEnum

  const [visibleSection, setVisibleSection] = useState<SectionParamOptionsEnum>(
    sectionParam || SectionParamOptionsEnum.grantees
  )

  const { goalGroupId } = useParams<{
    goalGroupId?: string
  }>()

  const goalGroupIdInt = goalGroupId ? parseInt(goalGroupId, 10) : null

  const { data, loading: loadingGrantees } = useQuery(
    GOAL_GROUP_GRANTEES_INFO,
    {
      variables: {
        funderOrgId,
        goalGroupId: goalGroupIdInt,
      },
      skip: !goalGroupId || !funderOrgId,
    }
  )

  const goalGroupGranteeInfo: GoalGroupGranteeInfo[] =
    data?.goalGroupGranteesInfo || []

  const nonprofitOrgIds = goalGroupGranteeInfo.map(
    (info) => info.nonprofitOrgId
  )
  const goalNames = goalGroupGranteeInfo.map((info) => info.goalName)

  const handleTabChange = (tab: SectionParamOptionsEnum) => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set(SECTION_PARAM_KEY, tab)
    history.replace({ search: searchParams.toString() })
    setVisibleSection(tab)
    if (goalGroupIdInt) {
      sendFunderGoalTabClickedEvent(
        tab,
        goalGroupIdInt,
        id,
        email,
        funderOrgId,
        nonprofitOrgIds,
        goalNames
      )
    }
  }

  const tabs: SectionTab[] = [
    {
      label: 'Grantees',
      param: SectionParamOptionsEnum.grantees,
      count: goalGroupGranteeInfo.length,
    },
    { label: 'Progress Chart', param: SectionParamOptionsEnum.progressChart },
    { label: 'Task Progress', param: SectionParamOptionsEnum.taskProgress },
  ]

  const menuTabs = tabs.map((tab) => {
    const countLabel = tab.count ? ` (${tab.count})` : ''
    return (
      <MenuTab
        key={tab.label}
        label={`${tab.label}${countLabel}`}
        value={tab.param}
      />
    )
  })

  const renderSection = () => {
    switch (visibleSection) {
      case 'progressChart':
        return (
          <ProgressChartSection
            goalGroupId={goalGroupIdInt}
            goalGroupTargetDescription={goalGroupTargetDescription}
          />
        )
      case 'taskProgress':
        return <TaskCompletionSection goalGroupId={goalGroupIdInt} />
      case 'grantees':
        return (
          <GranteesSection
            goalGroupGranteeInfo={goalGroupGranteeInfo}
            goalGroupTargetDescription={goalGroupTargetDescription}
            loading={loadingGrantees}
          />
        )
      default:
        return null
    }
  }

  const view = renderSection()

  return (
    <GoalSectionsContainer>
      <MenuTabs
        onChange={(_, value) => handleTabChange(value)}
        value={visibleSection}
      >
        {menuTabs}
      </MenuTabs>
      <Details>{view}</Details>
    </GoalSectionsContainer>
  )
}
